<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pointer-events-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div
        class="pointer-events-auto mx-auto w-full border border-default bg-default shadow-2xl"
        :class="preferencesOpen ? 'max-w-3xl rounded-2xl' : 'max-w-5xl rounded-xl'"
      >
        <div :class="preferencesOpen ? 'p-5 sm:p-6' : 'px-4 py-3 sm:px-5 sm:py-3.5'">
          <div class="flex items-start justify-between gap-4">
            <div class="min-w-0">
              <h2
                id="cookie-consent-title"
                :class="preferencesOpen ? 'text-lg font-semibold text-highlighted' : 'text-sm font-semibold text-highlighted'"
              >
                {{ preferencesOpen ? 'Preferințe cookie-uri' : 'Respectăm confidențialitatea ta' }}
              </h2>
              <p
                v-if="preferencesOpen"
                class="mt-2 text-sm leading-relaxed text-muted"
              >
                Folosim cookie-uri și tehnologii similare pentru funcționarea site-ului și, doar cu acordul tău,
                pentru analiză (ex. Google Analytics) și publicitate (ex. Meta Pixel).
                <NuxtLink :to="shopPath('/cms/politica-de-cookies')" class="text-primary-600 underline underline-offset-2 hover:text-primary-700">
                  Politica de cookies
                </NuxtLink>
                și
                <NuxtLink :to="shopPath('/cms/politica-de-confidentialitate')" class="text-primary-600 underline underline-offset-2 hover:text-primary-700">
                  Politica de confidențialitate
                </NuxtLink>.
              </p>
              <p
                v-else
                class="mt-1 text-xs text-muted"
              >
                Folosim cookie-uri necesare și, cu acordul tău, pentru analiză și marketing.
                <NuxtLink :to="shopPath('/cms/politica-de-cookies')" class="text-primary-600 underline underline-offset-2 hover:text-primary-700">
                  Detalii
                </NuxtLink>
              </p>
            </div>
            <UButton
              v-if="hasDecided && preferencesOpen"
              icon="i-heroicons-x-mark"
              color="neutral"
              variant="ghost"
              size="sm"
              aria-label="Închide"
              @click="closeBanner"
            />
          </div>

          <div v-if="preferencesOpen" class="mt-5 space-y-4">
            <div
              v-for="category in categories"
              :key="category.id"
              class="rounded-xl border border-muted bg-muted p-4"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <p class="font-medium text-highlighted">{{ category.label }}</p>
                  <p class="mt-1 text-sm text-muted">{{ category.description }}</p>
                </div>
                <USwitch
                  v-if="category.id === 'analytics'"
                  v-model="draftAnalytics"
                  :disabled="category.required"
                  :aria-label="category.label"
                />
                <USwitch
                  v-else-if="category.id === 'marketing'"
                  v-model="draftMarketing"
                  :disabled="category.required"
                  :aria-label="category.label"
                />
                <UBadge v-else color="neutral" variant="subtle" size="xs">
                  Activ
                </UBadge>
              </div>
            </div>
          </div>

          <div
            class="flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:justify-end"
            :class="preferencesOpen ? 'mt-5' : 'mt-3'"
          >
            <template v-if="preferencesOpen">
              <UButton
                label="Salvează preferințele"
                color="primary"
                class="sm:order-3"
                @click="saveDraft"
              />
              <UButton
                label="Accept toate"
                variant="solid"
                class="sm:order-2"
                @click="acceptAll"
              />
              <UButton
                label="Doar necesare"
                variant="ghost"
                color="neutral"
                class="sm:order-1"
                @click="rejectOptional"
              />
            </template>
            <template v-else>
              <UButton
                label="Accept toate"
                color="primary"
                size="sm"
                @click="acceptAll"
              />
              <UButton
                label="Doar necesare"
                variant="solid"
                size="sm"
                @click="rejectOptional"
              />
              <UButton
                label="Personalizează"
                variant="ghost"
                color="neutral"
                size="sm"
                @click="showCustomize"
              />
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const shopPath = useShopPath()
const route = useRoute()
const {
  bannerOpen,
  preferencesOpen,
  hasDecided,
  analytics,
  marketing,
  categories,
  hydrate,
  acceptAll,
  rejectOptional,
  savePreferences,
  closeBanner,
} = useCookieConsent()

const draftAnalytics = ref(false)
const draftMarketing = ref(false)
const hydrated = ref(false)

const visible = computed(() =>
  hydrated.value
  && bannerOpen.value
  && !route.path.startsWith('/admin')
  && !route.path.startsWith('/platform'),
)

watch(preferencesOpen, (open) => {
  if (!open) return
  draftAnalytics.value = analytics.value
  draftMarketing.value = marketing.value
})

watch(bannerOpen, (open) => {
  if (!open || preferencesOpen.value) return
  draftAnalytics.value = analytics.value
  draftMarketing.value = marketing.value
})

function saveDraft() {
  savePreferences(draftAnalytics.value, draftMarketing.value)
}

function showCustomize() {
  preferencesOpen.value = true
}

onMounted(() => {
  hydrate()
  // Defer until after typical LCP window so the hero (not the banner) is measured.
  const show = () => { hydrated.value = true }
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(show, { timeout: 4000 })
  }
  else {
    window.setTimeout(show, 4000)
  }
})
</script>
